import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { ScheduleType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/schedule.pb';
import { isEmpty } from 'lodash-es';
import { ScheduleAvailabilityTypes } from '@frontend/api-schedule-availability';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import {
  OfficeHoursOverrideBanner,
  OfficeHoursOverrideConfirmation,
  SCHEDULE_AVAILABILITY_HOURS_MAX_WIDTH,
  ScheduleAvailabilityHours,
  ScheduleAvailabilityHoursTimezoneHeader,
  ScheduleAvailabilityHoursValueRef,
} from '@frontend/schedule-availability-hours';
import { useLocationTimezone } from '@frontend/schedule-helpers';
import { theme } from '@frontend/theme';
import { Button, ButtonBar, useAlert, useModalControl } from '@frontend/design-system';
import {
  convertFromRRuleScheduleHoursToScheduleHoursObject,
  convertScheduleHoursObjectToRRuleObject,
} from '../../rrule-helper';
import { SETTINGS_TRACKING_IDS } from '../../trackingIds/settings-tracking-ids';

interface Props {
  locationId: string;
  providerId?: string;
  // this is used to pre-populate provider hours in case of create provider flow
  selectedProviderHours?: ScheduleAvailabilityTypes.ScheduleAvailabilityByDayOfWeek;
  onBack?: () => void;
  onContinue?: (scheduleAvailability: ScheduleAvailabilityTypes.ScheduleAvailabilityByDayOfWeek) => void;
}

export const ProviderAppointmentHours = ({
  locationId,
  providerId,
  selectedProviderHours,
  onBack,
  onContinue,
}: Props) => {
  const alert = useAlert();
  const { t } = useTranslation('scheduleGlobalSettings');
  const { locationTimezone } = useLocationTimezone(locationId);

  const [formState, setFormState] = useState<{ hasError: boolean; hasChanges: boolean }>({
    hasError: false,
    hasChanges: false,
  });
  const [isUsingOfficeHours, setIsUsingOfficeHours] = useState(false);
  const useOfficeHoursConfirmationModalControl = useModalControl({
    onClose: () => {
      if (!confirmUseOfficeHoursClickedRef.current) {
        setIsUsingOfficeHours(false);
      }
    },
  });
  const confirmUseOfficeHoursClickedRef = useRef(false);
  const availabilityHoursRef = useRef<ScheduleAvailabilityHoursValueRef>();

  const updateScheduleMutation = SchedulerV3.Mutations.useUpdateScheduleMutation({
    options: {
      onSuccess: () => {
        alert.success(t('Provider schedule updated successfully'));
      },
      onError: () => {
        alert.error(t('Failed to update Provider schedule'));
      },
    },
  });
  const createScheduleMutation = SchedulerV3.Mutations.useCreateScheduleMutation({
    options: {
      onSuccess: () => {
        alert.success(t('Provider schedule created successfully'));
      },
      onError: () => {
        alert.error(t('Failed to create Provider schedule'));
      },
    },
  });
  const officeScheduleQuery = SchedulerV3.Queries.useListSchedulesQuery({
    request: { locationId, ids: [locationId] },
    options: { enabled: !!locationId },
  });
  const providerScheduleQuery = SchedulerV3.Queries.useListSchedulesQuery({
    request: { locationId, ids: providerId ? [providerId] : [] },
    options: { enabled: !!locationId && !!providerId },
  });

  const handleAvailabilityHoursChange = useCallback(
    (hasChanges: boolean, hasError: boolean) => setFormState({ hasChanges, hasError }),
    []
  );
  const officeScheduledHours = useMemo(() => {
    const locationSchedule = officeScheduleQuery.data?.schedules?.[0]?.recurrenceRules;

    return convertFromRRuleScheduleHoursToScheduleHoursObject({
      workingHours: locationSchedule?.workingHours || [],
      breaks: locationSchedule?.breaks || [],
    });
  }, [officeScheduleQuery.data]);

  const { hasProviderSchedule, providerScheduledHours } = useMemo(() => {
    // Note: as confirm click will refetch provider schedule which reset on confirm click
    // we need to reset the confirm click ref to false here
    confirmUseOfficeHoursClickedRef.current = false;

    if (!providerId) {
      // TODO: may be we need to check each day of week for provider schedule (selectedProviderHours)
      const hasProviderSchedule = !isEmpty(selectedProviderHours);
      if (!hasProviderSchedule) {
        // if no provider schedule then use office hours
        return { providerScheduledHours: {}, hasProviderSchedule };
      }

      return {
        providerScheduledHours: selectedProviderHours,
        hasProviderSchedule,
      };
    }

    const providerSchedule = providerScheduleQuery.data?.schedules?.[0]?.recurrenceRules;

    const hasProviderSchedule = !!providerSchedule?.workingHours?.length;

    if (!hasProviderSchedule) return { providerScheduledHours: {}, hasProviderSchedule };

    const providerScheduledHours = convertFromRRuleScheduleHoursToScheduleHoursObject({
      workingHours: providerSchedule?.workingHours || [],
      breaks: providerSchedule?.breaks || [],
    });

    return { providerScheduledHours, hasProviderSchedule };
  }, [providerScheduleQuery.data, selectedProviderHours, providerId]);

  // Set the initial state for hasOverriddenOfficeHours based on whether the provider has a schedule
  useEffect(() => {
    setIsUsingOfficeHours(!hasProviderSchedule);
  }, [hasProviderSchedule]);

  const handleToggleOfficeHoursOverride = () => {
    if (!isUsingOfficeHours) {
      if (providerId && hasProviderSchedule) {
        // was not using office schedule & was having provider schedule then confirm to reset provider schedule
        useOfficeHoursConfirmationModalControl.openModal();
      } else {
        // was not using office schedule & was not having provider schedule then reset availability hours state
        availabilityHoursRef.current?.resetAvailabilityHours();
      }
    }

    setIsUsingOfficeHours((prev) => !prev);
  };
  const handleConfirmOfficeHoursOverride = async () => {
    // reset provider schedule so that it uses office hours
    await handleUpdateProviderSchedule({});
    confirmUseOfficeHoursClickedRef.current = true;
    useOfficeHoursConfirmationModalControl.closeModal();
  };
  const handleCancelClick = () => {
    if (hasFormChanges) {
      if (formState.hasChanges) {
        availabilityHoursRef.current?.resetAvailabilityHours();
      } else if (!hasProviderSchedule) {
        setIsUsingOfficeHours(true);
      }
    }
  };
  const handleBackClick = () => {
    handleCancelClick();
    onBack?.();
  };
  const handleContinueClick = () => {
    const providerAppointmentHours: ScheduleAvailabilityTypes.ScheduleAvailabilityByDayOfWeek = isUsingOfficeHours
      ? {}
      : availabilityHoursRef.current?.getUpdatedScheduleInfo() ?? {};

    onContinue?.(providerAppointmentHours);
  };
  const handleSave = () => {
    const updatedScheduleAvailability = availabilityHoursRef.current?.getUpdatedScheduleInfo();
    if (!updatedScheduleAvailability) {
      return;
    }

    if (providerScheduleQuery.data?.schedules?.length) {
      handleUpdateProviderSchedule(updatedScheduleAvailability);
    } else {
      const { workingHours, breaks } = convertScheduleHoursObjectToRRuleObject(
        updatedScheduleAvailability,
        locationTimezone
      );
      if (providerId) {
        createScheduleMutation.mutate({
          id: providerId,
          locationId,
          type: ScheduleType.PROVIDER,
          recurrenceRules: { workingHours, breaks },
        });
      }
    }
  };
  const handleUpdateProviderSchedule = useCallback(
    (availability: ScheduleAvailabilityTypes.ScheduleAvailabilityByDayOfWeek) => {
      const providerSchedule = providerScheduleQuery.data?.schedules?.[0];
      // don't do anything if there is no provider schedule
      if (!providerSchedule) return;

      const { workingHours, breaks } = convertScheduleHoursObjectToRRuleObject(availability, locationTimezone);

      return updateScheduleMutation.mutateAsync({
        id: providerSchedule.id,
        locationId,
        type: providerSchedule.type || ScheduleType.PROVIDER,
        recurrenceRules: {
          ...providerScheduleQuery.data?.schedules[0]?.recurrenceRules,
          workingHours,
          breaks,
        },
      });
    },
    [locationId, locationTimezone, providerScheduleQuery.data, updateScheduleMutation]
  );

  // if no provider schedule then use office hours
  // and if use office hours toggled then display office hours temporarily until saved
  const scheduleInfo = hasProviderSchedule
    ? isUsingOfficeHours
      ? officeScheduledHours
      : providerScheduledHours
    : officeScheduledHours;

  const isLoading = officeScheduleQuery.isLoading || providerScheduleQuery.isLoading;

  const isSaving = createScheduleMutation.isLoading || updateScheduleMutation.isLoading;
  const hasFormChanges = formState.hasChanges || (!hasProviderSchedule && !isUsingOfficeHours);
  const shouldDisableSave = formState.hasError || isSaving || !hasFormChanges;
  return (
    <section
      css={css({
        position: 'relative',
        maxHeight: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      })}
    >
      <OfficeHoursOverrideBanner
        isUsingOfficeHours={isUsingOfficeHours}
        onToggleOverride={handleToggleOfficeHoursOverride}
      />
      <OfficeHoursOverrideConfirmation
        {...useOfficeHoursConfirmationModalControl.modalProps}
        onConfirm={handleConfirmOfficeHoursOverride}
      />
      <ScheduleAvailabilityHoursTimezoneHeader locationId={locationId} />
      <div css={{ overflowY: 'auto', flex: 1, paddingBottom: providerId ? 0 : 72 }}>
        {/* using fieldset to disable all inputs in ScheduleAvailabilityHours component */}
        <fieldset
          disabled={isLoading || isUsingOfficeHours}
          css={[{ border: 'none', padding: 0, opacity: isUsingOfficeHours ? 0.5 : 1 }]}
        >
          <ScheduleAvailabilityHours
            scheduleInfo={scheduleInfo}
            ref={availabilityHoursRef}
            closedOfficeText={t('Out of Office')}
            isLoading={providerScheduleQuery.isLoading}
            onChange={handleAvailabilityHoursChange}
          />
        </fieldset>
      </div>
      {providerId ? (
        !isUsingOfficeHours && (
          <ButtonBar css={buttonBarStyle}>
            <Button variant='secondary' disabled={isSaving} onClick={handleCancelClick} size='large'>
              {t('Cancel')}
            </Button>
            <Button
              disabled={shouldDisableSave}
              loading={isSaving}
              onClick={handleSave}
              size='large'
              trackingId={SETTINGS_TRACKING_IDS.manageProviders.saveScheduleButton}
            >
              {t('Save')}
            </Button>
          </ButtonBar>
        )
      ) : (
        <ButtonBar css={[buttonBarStyle, { paddingBottom: 0 }]}>
          <Button variant='secondary' disabled={isSaving} onClick={handleBackClick} size='large'>
            {t('Back')}
          </Button>
          <Button disabled={formState.hasError} onClick={handleContinueClick} size='large'>
            {t('Continue')}
          </Button>
        </ButtonBar>
      )}
    </section>
  );
};

const buttonBarStyle = css({
  borderTop: `thin solid ${theme.colors.neutral20}`,
  maxWidth: SCHEDULE_AVAILABILITY_HOURS_MAX_WIDTH,
});
